import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Book, Users, UserPlus, UserMinus, ArrowLeft, Eye, Heart } from '@phosphor-icons/react';
import { toast } from 'sonner';
import GlobalHeader from '../components/GlobalHeader';

export const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [articles, setArticles] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [profileRes, articlesRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/articles?author_id=${userId}&status=published`)
        ]);
        setProfile(profileRes.data);
        setArticles(articlesRes.data);

        if (currentUser && currentUser.user_id !== userId) {
          const followRes = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/is-following`,
            { withCredentials: true }
          );
          setIsFollowing(followRes.data.following);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId, currentUser]);

  const handleFollow = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/follow`,
        {},
        { withCredentials: true }
      );
      setIsFollowing(res.data.following);
      setProfile({
        ...profile,
        followers_count: profile.followers_count + (res.data.following ? 1 : -1)
      });
      toast.success(res.data.following ? `Following ${profile.name}` : `Unfollowed ${profile.name}`);
    } catch (error) {
      toast.error('Failed to update follow status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <GlobalHeader />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader />
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">User not found</p>
            <Link to="/" className="text-primary hover:underline">Go home</Link>
          </div>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser && currentUser.user_id === userId;

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/" data-testid="back-button" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft size={16} />
            Back to Home
          </Link>

          {/* Profile Header */}
          <div className="bg-card border border-border rounded-lg p-8 mb-8">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <img 
                src={profile.picture || `https://ui-avatars.com/api/?name=${profile.name}&size=200`}
                alt={profile.name}
                className="w-32 h-32 rounded-full border-4 border-border"
                data-testid="profile-picture"
              />
              
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-4xl tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }} data-testid="profile-name">
                    {profile.name}
                  </h2>
                  {profile.role === 'admin' && (
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded-full font-semibold uppercase tracking-widest">Admin</span>
                  )}
                  {profile.role === 'editor' && (
                    <span className="text-xs bg-amber-500 text-white px-2 py-1 rounded-full font-semibold uppercase tracking-widest">Editor</span>
                  )}
                </div>
                
                <p className="text-muted-foreground mb-4" data-testid="profile-bio">
                  {profile.bio || 'No bio yet'}
                </p>

                <div className="flex items-center gap-6 mb-6">
                  <div data-testid="followers-count">
                    <p className="text-2xl font-bold">{profile.followers_count}</p>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Followers</p>
                  </div>
                  <div data-testid="following-count">
                    <p className="text-2xl font-bold">{profile.following_count}</p>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Following</p>
                  </div>
                  <div data-testid="articles-count">
                    <p className="text-2xl font-bold">{articles.length}</p>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Articles</p>
                  </div>
                  <div data-testid="points-count">
                    <p className="text-2xl font-bold">{profile.points}</p>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Points</p>
                  </div>
                </div>

                {!isOwnProfile && (
                  <button
                    onClick={handleFollow}
                    data-testid="follow-button"
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold transition-transform hover:scale-[1.02] ${
                      isFollowing ? 'bg-muted text-foreground border border-border' : 'bg-primary text-primary-foreground'
                    }`}
                  >
                    {isFollowing ? <UserMinus size={20} /> : <UserPlus size={20} />}
                    {isFollowing ? 'Following' : 'Follow'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Published Articles */}
          <h3 className="text-3xl mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
            Published Works
          </h3>

          {articles.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-16 text-center" data-testid="no-articles">
              <Book size={48} weight="duotone" className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No published articles yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {articles.map((article) => (
                <Link
                  key={article.article_id}
                  to={`/article/${article.article_id}`}
                  data-testid={`profile-article-${article.article_id}`}
                  className="bg-card border border-border rounded-lg p-6 hover:border-primary transition-colors"
                >
                  <span className="text-xs font-semibold uppercase tracking-widest text-primary mb-3 block">{article.category}</span>
                  <h4 className="text-xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
                    {article.title}
                  </h4>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{article.meta_description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Eye size={14} />
                      {article.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <Heart size={14} />
                      {article.likes}
                    </span>
                    <span>{article.reading_time} min read</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
